// Shared plate-georef confidence badge.
//
// Extracted from ProcedureTab.tsx on 2026-05-16 Track 2 so PlanPanel,
// PlatesPage, and the Procedure Brief panel can render the same badge
// without duplicating the legend.
//
// Quality legend:
//   ✓ OK      — vision-affine fit at residual < 0.25 NM
//   △ LC      — low-confidence fit (residual 0.25–1 NM, still usable but check)
//   ⊘ Fail    — no automated fit (overlay falls back to CIFP bbox)
//   ✎ Fixed   — cartographer-approved manual correction (overlay uses it)
//   ⧖ Pending — manual correction submitted, awaiting cartographer approval;
//                map overlay still uses the original (failed/LC) georef
//   (no badge for 'unknown' — plate hasn't been touched by the pipeline)

import type { PlateGeorefQuality } from '../lib/plates';

export interface GeorefBadgeProps {
  quality: PlateGeorefQuality;
  /** Render the long word ("OK", "LC", "Fail", "Fixed") next to the glyph.
   *  Default: true. Pass false for cramped inline contexts (plan-row). */
  showText?: boolean;
  /** Extra Tailwind classes (e.g. for font-size tweaks). */
  className?: string;
}

export function GeorefBadge({
  quality,
  showText = true,
  className = '',
}: GeorefBadgeProps) {
  if (quality === 'unknown') return null;
  const cfg = {
    ok:               { sym: '✓',  text: 'OK',      cls: 'bg-emerald-700/40 text-emerald-300 border-emerald-600/50' },
    'low-confidence': { sym: '△',  text: 'LC',      cls: 'bg-amber-700/40 text-amber-200 border-amber-600/50'      },
    failed:           { sym: '⊘',  text: 'Fail',    cls: 'bg-red-800/50 text-red-300 border-red-700/60'             },
    corrected:        { sym: '✎',  text: 'Fixed',   cls: 'bg-sky-700/40 text-sky-200 border-sky-600/50'             },
    'pending-review': { sym: '⧖',  text: 'Pending', cls: 'bg-violet-700/40 text-violet-200 border-violet-600/50'    },
  }[quality];
  const title =
    quality === 'ok' ? 'Plate is georeferenced and ready for map overlay' :
    quality === 'low-confidence' ? 'Georef low confidence — verify against actual plate before relying on overlay' :
    quality === 'failed' ? 'No automated georef — map overlay will use a rough CIFP bbox' :
    quality === 'corrected' ? 'Plate has a cartographer-approved manual correction' :
    'Manual correction submitted — awaiting cartographer approval. Overlay still uses the original georef.';
  return (
    <span
      title={title}
      className={`shrink-0 inline-flex items-center gap-0.5 rounded px-1 py-0 text-[9px] font-mono uppercase tracking-wide border ${cfg.cls} ${className}`}
    >
      <span aria-hidden="true">{cfg.sym}</span>
      {showText && <span>{cfg.text}</span>}
    </span>
  );
}
